import { useEffect, useState } from "react";
import { Plus, CalendarDays } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const PRIO_CLS = {
  high: "bg-red-500/10 text-red-600 border-red-500/30",
  medium: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  low: "bg-slate-500/10 text-slate-500 border-slate-400/30",
};

function TaskCard({ task, onStatus, t }) {
  return (
    <Card className="rounded-[14px] card-hover" data-testid={`task-card-${task.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium leading-snug">{task.title}</p>
          <Badge variant="outline" className={`rounded-full text-[10px] shrink-0 ${PRIO_CLS[task.priority] || PRIO_CLS.medium}`}>
            {task.priority}
          </Badge>
        </div>
        {task.description && <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-2">{task.description}</p>}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
            <CalendarDays className="h-3 w-3" />
            {task.due_date ? new Date(task.due_date).toLocaleDateString() : "\u2014"}
          </span>
          <Select value={task.status} onValueChange={(v) => onStatus(task.id, v)}>
            <SelectTrigger className="h-7 w-[120px] rounded-[8px] text-xs" data-testid={`task-status-select-${task.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">{t("Offen", "Open")}</SelectItem>
              <SelectItem value="doing">{t("In Arbeit", "Doing")}</SelectItem>
              <SelectItem value="done">{t("Erledigt", "Done")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {task.assigned_to_name && <p className="mt-2 text-[10px] text-muted-foreground">{task.assigned_to_name}</p>}
      </CardContent>
    </Card>
  );
}

export default function Tasks() {
  const { t } = useLang();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", due_date: "" });

  const load = () => {
    api.get("/portal/tasks").then((r) => setTasks(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/portal/tasks/${id}`, { status });
      setTasks((ts) => ts.map((task) => (task.id === id ? { ...task, status } : task)));
    } catch (e) {
      toast.error(t("Aktualisierung fehlgeschlagen", "Update failed"));
    }
  };

  const create = async () => {
    if (!form.title.trim()) {
      toast.error(t("Titel erforderlich", "Title required"));
      return;
    }
    try {
      const res = await api.post("/portal/tasks", { ...form, due_date: form.due_date || null });
      setTasks((ts) => [res.data, ...ts]);
      setCreateOpen(false);
      setForm({ title: "", description: "", priority: "medium", due_date: "" });
      toast.success(t("Aufgabe erstellt", "Task created"));
    } catch (e) {
      toast.error(t("Aufgabe konnte nicht erstellt werden", "Task could not be created"));
    }
  };

  const groups = {
    open: tasks.filter((task) => task.status === "open"),
    doing: tasks.filter((task) => task.status === "doing"),
    done: tasks.filter((task) => task.status === "done"),
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{t("Aufgaben", "Tasks")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("Ihre pers\u00f6nliche Aufgabenliste", "Your personal task list")}</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-[10px] bg-[#0B1220]" data-testid="task-create-button">
              <Plus className="h-4 w-4 mr-1.5" /> {t("Neue Aufgabe", "New task")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t("Neue Aufgabe", "New task")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t("Titel", "Title")} *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-[10px]" data-testid="task-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("Beschreibung", "Description")}</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-[10px]" data-testid="task-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t("Priorit\u00e4t", "Priority")}</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="rounded-[10px]" data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">{t("Hoch", "High")}</SelectItem>
                      <SelectItem value="medium">{t("Mittel", "Medium")}</SelectItem>
                      <SelectItem value="low">{t("Niedrig", "Low")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("F\u00e4llig am", "Due date")}</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-[10px]" data-testid="task-due-input" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-[10px] bg-[#0B1220]" data-testid="task-create-submit">{t("Erstellen", "Create")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-48 rounded-[16px]" />)}
        </div>
      ) : (
        <Tabs defaultValue="board" data-testid="tasks-board">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              ["open", t("Offen", "Open")],
              ["doing", t("In Arbeit", "In progress")],
              ["done", t("Erledigt", "Done")],
            ].map(([key, label]) => (
              <div key={key}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold">{label}</h3>
                  <Badge variant="secondary" className="rounded-full text-[10px] font-mono-num">{groups[key].length}</Badge>
                </div>
                <div className="space-y-3">
                  {groups[key].length === 0 && (
                    <div className="rounded-[14px] border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                      {t("Keine Aufgaben", "No tasks")}
                    </div>
                  )}
                  {groups[key].map((task) => (
                    <TaskCard key={task.id} task={task} onStatus={updateStatus} t={t} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Tabs>
      )}
    </div>
  );
}
